/**
 * Brand Logo Component
 *
 * Unified brand identity with "AI" badge and "Commerce Ready" text.
 * Supports responsive sizing and visibility.
 */


import { cn } from '@/lib/utils'

interface BrandLogoProps {
  /**
   * Size variant
   * - default: h-8 badge (32px) - for navbar
   * - compact: h-6 badge (24px) - for mobile/condensed views
   * - large: h-10 badge (40px) - for headers
   */
  readonly size?: 'compact' | 'default' | 'large'

  /**
   * Show "Commerce Ready" text
   * @default true
   */
  readonly showText?: boolean

  /**
   * Text visibility breakpoint
   * @default 'sm' (show on sm+ screens)
   */
  readonly textBreakpoint?: 'always' | 'sm' | 'md' | 'lg'

  /**
   * Additional CSS classes
   */
  readonly className?: string

  /**
   * Click handler
   */
  readonly onClick?: () => void

  /**
   * Link href (makes logo clickable)
   */
  readonly href?: string
}

const SIZE_CONFIG = {
  compact: {
    badge: 'h-6 w-6',
    badgeText: 'text-sm',
    text: 'text-base',
  },
  default: {
    badge: 'h-8 w-8',
    badgeText: 'text-lg',
    text: 'text-lg',
  },
  large: {
    badge: 'h-10 w-10',
    badgeText: 'text-xl',
    text: 'text-xl',
  },
} as const

const BREAKPOINT_CLASSES = {
  always: '',
  sm: 'hidden sm:inline',
  md: 'hidden md:inline',
  lg: 'hidden lg:inline',
} as const

export function BrandLogo({
  size = 'default',
  showText = true,
  textBreakpoint = 'sm',
  className,
  onClick,
  href,
}: BrandLogoProps) {
  const sizeConfig = SIZE_CONFIG[size]

  const logoContent = (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className={cn(
          'flex items-center justify-center rounded-lg bg-primary',
          sizeConfig.badge
        )}
      >
        <span className={cn('font-bold text-primary-foreground', sizeConfig.badgeText)}>
          AI
        </span>
      </div>

      {showText && (
        <span
          className={cn(
            'font-bold text-foreground',
            sizeConfig.text,
            BREAKPOINT_CLASSES[textBreakpoint]
          )}
        >
          Commerce Ready
        </span>
      )}
    </div>
  )

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className="transition-opacity hover:opacity-80"
      >
        {logoContent}
      </a>
    )
  }

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="transition-opacity hover:opacity-80"
        type="button"
      >
        {logoContent}
      </button>
    )
  }

  return logoContent
}
